import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { readBootstrapState } from "../../lib/bootstrap-state.js";
import { readStatusBlob, type StatusDoc } from "../../lib/bootstrap-status.js";
import { getAciState } from "../../lib/bootstrap-aci-state.js";
import { finalizeInstall } from "../../lib/bootstrap-finalize.js";

export class BootstrapStatusCommand extends M8tCommand {
  // `bootstrap finish` is a DEPRECATED ALIAS, not a second command. Everything it
  // used to do either moved into the cloud installer (the founder's data-plane
  // grant, now at entrypoint.sh step 3b) or into this command's own `done`
  // handling. The path stays registered because it is written down in guides,
  // runbooks and shakedown recipes that a founder may already be part-way
  // through — it prints a deprecation notice and does the right thing.
  static paths = [["bootstrap", "status"], ["bootstrap", "finish"]];
  static usage = Command.Usage({
    description: "Show the cloud installer's live status (phase, progress, result) — and finish the local setup when it lands.",
    details:
      "Reads the durable status blob written by the installer. --watch polls until the install reaches done or failed.\n\n" +
      "On reaching done under --watch this also completes the local half of the install, which nothing else in the bootstrap path can do: it registers the webapp's sign-in redirect URI (the installer runs as a managed identity with no directory role, and at launch time the gateway FQDN did not exist yet), writes ~/.m8t/repo-root and the gateway discovery cache, and seeds your advisors' brains from the onboarding intake. Re-runnable — `m8t bootstrap finish` is a deprecated alias that does exactly this on an already-done install.",
    examples: [
      ["One read", "$0 bootstrap status"],
      ["Watch to completion, then finish the local setup", "$0 bootstrap status --watch"],
      ["Redo the local setup on a finished install", "$0 bootstrap status --finalize --repo-root /path/to/m8t"],
    ],
  });

  watch = Option.Boolean("--watch", false);
  output = Option.String("--output");
  repoRoot = Option.String("--repo-root", { description: "The m8t clone to point local tools at (default: the current directory)." });
  finalize = Option.Boolean("--finalize", false, { description: "Complete the local setup against an already-done install, without watching." });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");

  async executeCommand(): Promise<number> {
    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({
        code: "BOOTSTRAP_NO_STATE",
        message: "No bootstrap is in flight (no ~/.m8t/bootstrap.json).",
        hint: "Run 'm8t bootstrap launch --location <region>' first.",
      });
    }
    // Standard convention: auto → pretty on a TTY, json when piped/redirected
    // (matches every other m8t command). No bespoke default.
    const mode = resolveOutputMode(
      typeof this.output === "string" ? (this.output as "pretty" | "json" | "auto") : undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
    const watch = this.watch === true;
    // `this.path` is the path clipanion actually matched, so the alias can
    // announce itself without a second command class. clipanion only assigns it
    // at dispatch, so it is genuinely absent under a direct instantiation (the
    // unit-test harness) — read it defensively rather than trusting the type.
    const invokedPath = (this.path as string[] | undefined) ?? [];
    const viaFinishAlias = invokedPath[invokedPath.length - 1] === "finish";
    if (viaFinishAlias) {
      this.context.stderr.write(
        `  ${colors.hint("note:")} ${colors.dim("`m8t bootstrap finish` is deprecated — `m8t bootstrap status --watch` now finishes the local setup itself when the install lands.")}\n`,
      );
    }
    // A bare `m8t bootstrap status` is a peek and must stay read-only: it is what
    // an operator runs to look at someone else's in-flight install. Finalizing
    // writes to the directory and to ~/.m8t, so it is gated on asking for it —
    // by watching the install to completion, by the deprecated alias, or by
    // --finalize on an install that is already done.
    const wantsFinalize = watch || viaFinishAlias || this.finalize === true;

    for (;;) {
      let doc: StatusDoc;
      try {
        doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
      } catch (e) {
        // The blob doesn't exist until the installer's phase 1 (status storage),
        // which lands a minute or so after the ACI cold-starts. In --watch we
        // tolerate that startup window and keep polling; a single read still errors.
        if (watch && e instanceof LocalCliError && e.code === "BOOTSTRAP_STATUS_UNREADABLE") {
          // ...but an abort BEFORE phase 1 - a missing required variable, say -
          // dies with no storage account to publish to, so no blob will ever
          // appear. A terminated container is the only evidence available on that
          // path; without this check the loop waits forever on a run that is over.
          const early = await getAciState({
            aciName: state.aciName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId,
          }).catch(() => null);
          if (early?.terminated) {
            this.context.stderr.write(
              `  ${colors.error("✗")} the installer container has terminated (exit ${String(early.exitCode ?? "?")}) and never reported a status.\n` +
              `  ${colors.hint("inspect:")} az container logs -n ${state.aciName} -g ${state.resourceGroup}\n` +
              `  ${colors.hint("reap:")} m8t bootstrap reap --force\n`,
            );
            return 1;
          }
          if (mode === "pretty") this.context.stderr.write(`  ${colors.dim("waiting for the installer to start…")}\n`);
          await sleep(10_000);
          continue;
        }
        throw e;
      }
      if (!watch || doc.status !== "running") {
        if (mode === "json") { this.context.stdout.write(renderJson(doc) + "\n"); }
        else { this.context.stdout.write(formatStatus(doc)); }
        if (wantsFinalize) {
          if (doc.status === "done") {
            await this.runFinalize(doc, state.subscriptionId, state.resourceGroup, mode);
          } else if (this.finalize === true || viaFinishAlias) {
            // Asked for explicitly against an install that is not done. The old
            // `finish` refused here rather than half-configuring a machine
            // against a platform that may not exist; keep that refusal.
            throw new LocalCliError({
              code: "BOOTSTRAP_FINISH_NOT_DONE",
              message: `The platform is not live yet (status: ${doc.status}, phase: ${doc.phase}).`,
              hint: "Wait for 'm8t bootstrap status --watch' to reach done — it finishes the local setup itself.",
            });
          }
        }
        return doc.status === "failed" ? 1 : 0;
      }
      if (mode === "pretty") this.context.stderr.write(`  ${colors.dim(`${doc.phase} (${String(doc.phaseIndex)}/${String(doc.phaseTotal)}) ${doc.detail ?? ""}`)}\n`);
      const aci = await getAciState({ aciName: state.aciName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId }).catch(() => null);
      if (aci?.terminated) {
        this.context.stderr.write(
          `  ${colors.error("✗")} the installer container has terminated (exit ${String(aci.exitCode ?? "?")}) but the status is still '${doc.phase}'.\n` +
          `  ${colors.hint("inspect:")} az container logs -n ${state.aciName} -g ${state.resourceGroup}\n` +
          `  ${colors.hint("reap:")} m8t bootstrap reap --force\n`,
        );
        return 1;
      }
      await sleep(10_000);
    }
  }

  /**
   * Complete the local half of the install. Never throws: the platform is live,
   * and a failure to point one machine at it must not turn a successful install
   * into a non-zero exit — every step inside reports its own failure with the
   * remedy, and the outer catch covers the ones that cannot.
   *
   * In `--output json` mode stdout carries the status document and nothing else,
   * so all of finalize's prose is routed to stderr rather than corrupting it.
   */
  private async runFinalize(doc: StatusDoc, subscriptionId: string, resourceGroup: string, mode: "pretty" | "json"): Promise<void> {
    const toStderr = (s: string) => this.context.stderr.write(s);
    try {
      await finalizeInstall({
        doc,
        subscriptionId: (typeof this.subscription === "string" ? this.subscription : undefined) ?? subscriptionId,
        resourceGroup: (typeof this.resourceGroup === "string" ? this.resourceGroup : undefined) ?? resourceGroup,
        ...(typeof this.repoRoot === "string" ? { repoRoot: this.repoRoot } : {}),
        stdout: mode === "json" ? toStderr : (s) => this.context.stdout.write(s),
        stderr: toStderr,
      });
    } catch (e) {
      this.context.stderr.write(
        `  ${colors.error("⚠ the platform is live, but the local setup did not complete:")} ${(e as Error).message}\n` +
        `  ${colors.hint("retry:")} m8t bootstrap status --finalize\n`,
      );
    }
  }
}

/** Exported for tests — pure rendering, no I/O. */
export function formatStatus(d: StatusDoc): string {
  const head = `${d.status.toUpperCase()} — ${d.phase} (${String(d.phaseIndex)}/${String(d.phaseTotal)})${d.detail ? ` — ${d.detail}` : ""}\n`;
  if (d.status === "done" && d.result) {
    return colors.success("✓ ") + head +
      `  gateway:  ${d.result.gatewayUrl ?? "-"}\n` +
      `  foundry:  ${d.result.foundryEndpoint ?? "-"}\n` +
      `  ${colors.hint("next:")} m8t bootstrap reap\n`;
  }
  if (d.status === "failed" && d.error) {
    // Both fields are optional because the installer's exit trap can publish a
    // bare `{}` when it cannot compose them — the fallback that guarantees a
    // dead install still reports `failed` instead of freezing on `running`.
    // Say so plainly rather than rendering "error in undefined: undefined";
    // the phase is still on the document's own `phase` field either way.
    const where = d.error.phase ?? d.phase;
    const why = d.error.message ?? "the installer did not record why — check the container logs";
    return colors.error("✗ ") + head + `  ${colors.error(`error in ${where}: ${why}`)}\n  ${colors.hint("inspect:")} az container logs\n`;
  }
  return head;
}
